import React, { useState, useEffect } from "react";
import { useI18n } from '../context/I18nContext';
import api from "../services/api";
import { SendIcon, AlertIcon, CheckIcon, RefreshIcon } from "../components/Icons";
import LogPanel from "../components/LogPanel";
import useApiLogs from "../hooks/useApiLogs";

const WhatsApp = () => {
  const { t } = useI18n();
  const [templates, setTemplates] = useState([]);
  const [connections, setConnections] = useState([]);
  const [formData, setFormData] = useState({
    templateName: "", templateLanguage: "", connectionId: "", recipientPhone: ""
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const { logs, addLog, clearLogs } = useApiLogs();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      addLog("info", t('whatsapp.a1'));
      const [templatesRes, connectionsRes] = await Promise.all([
        api.get("/templates"), api.get("/connections")
      ]);
      setTemplates(templatesRes.data);
      setConnections(connectionsRes.data);
      addLog("success", templatesRes.data.length + " templates carregados");
      addLog("success", connectionsRes.data.length + " conexoes ativas");
      if (connectionsRes.data.length > 0) {
        setFormData(prev => ({ ...prev, connectionId: connectionsRes.data[0].id }));
      }
    } catch (error) {
      addLog("error", t('whatsapp.a2'), error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });
    const conn = connections.find(c => c.id === parseInt(formData.connectionId));
    addLog("info", t('log.sepSendTemplate'));
    addLog("info", "Template: " + formData.templateName);
    addLog("info", "Destinatario: " + formData.recipientPhone);
    addLog("info", t('whatsapp.a3') + (conn?.name || formData.connectionId));

    try {
      const response = await api.post("/whatsapp/send", {
        connectionId: formData.connectionId, recipientPhone: formData.recipientPhone,
        templateName: formData.templateName, languageCode: formData.templateLanguage || "en_US"
      });
      if (response.data.metaRequest) addLog("request", "REQUEST:", JSON.stringify(response.data.metaRequest, null, 2));
      addLog("success", t('whatsapp.a4') + response.data.messageId);
      if (response.data.metaResponse) addLog("success", "RESPONSE:", JSON.stringify(response.data.metaResponse, null, 2));
      setMessage({ type: "success", text: t('whatsapp.a5') + response.data.messageId });
      setFormData(prev => ({ ...prev, recipientPhone: "" }));
    } catch (error) {
      const errorMsg = error.response?.data?.error || t('whatsapp.a6');
      if (error.response?.data?.metaRequest) addLog("request", "REQUEST:", JSON.stringify(error.response.data.metaRequest, null, 2));
      addLog("error", t('whatsapp.a7') + errorMsg);
      if (error.response?.data?.metaError) addLog("error", "ERROR:", JSON.stringify(error.response.data.metaError, null, 2));
      setMessage({ type: "error", text: errorMsg });
    }
    setLoading(false);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('wa.title')}</h1>
          <p className="text-gray-500 mt-1">{t('wa.subtitle')}</p>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
          <RefreshIcon className="w-5 h-5" /><span className="text-sm font-medium">{t('wa.refresh')}</span>
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('wa.send')}</h2>
              <p className="text-gray-600">{t('wa.subtitleShort')}</p>
            </div>
            {message.text && (
              <div className={"mb-6 p-4 rounded-xl flex items-start gap-3 " + (message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200")}>
                {message.type === "success" ? <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <span>{message.text}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('wa.template')}</label>
                <select value={formData.templateName} onChange={(e) => { const t = templates.find(t => t.name === e.target.value); setFormData({...formData, templateName: e.target.value, templateLanguage: t?.language || "en_US"}); }} className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none appearance-none cursor-pointer" required>
                  <option value="">{t('wa.pickTemplate')}</option>
                  {templates.filter(t => t.status === "APPROVED").map(t => (<option key={t.id} value={t.name}>{t.name} ({t.language})</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('wa.sender')}</label>
                <select value={formData.connectionId} onChange={(e) => setFormData({...formData, connectionId: e.target.value})} className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none appearance-none cursor-pointer" required>
                  <option value="">{t('wa.pick')}</option>
                  {connections.map(c => (<option key={c.id} value={c.id}>{c.name} - {c.phoneNumberId}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">{t('wa.recipientPhone')}</label>
                <input type="tel" value={formData.recipientPhone} onChange={(e) => setFormData({...formData, recipientPhone: e.target.value})} placeholder="+55 (14) 9 9200-9701" className="w-full h-12 px-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-whatsapp focus:border-whatsapp outline-none" required />
              </div>
              <div className="flex justify-end gap-4 pt-4">
                <button type="button" onClick={() => setFormData({templateName:"", templateLanguage:"", connectionId: connections[0]?.id || "", recipientPhone:""})} className="px-6 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition-colors">{t('wa.clear')}</button>
                <button type="submit" disabled={loading} className="flex items-center gap-2 px-6 py-3 bg-whatsapp text-white font-semibold rounded-xl hover:bg-whatsapp-dark transition-all shadow-lg shadow-whatsapp/20 disabled:opacity-50"><SendIcon className="w-5 h-5" />{loading ? t('whatsapp.a8') : t('whatsapp.a9')}</button>
              </div>
            </form>
          </div>
        </div>
        <div className="lg:col-span-2">
          <LogPanel logs={logs} onClear={clearLogs} title={t('wa.logs')} />
        </div>
      </div>
    </div>
  );
};

export default WhatsApp;
